import React from 'react';
import PropTypes from 'prop-types';
import Cookies from 'cookies-js';
import styled from 'styled-components';
import { VerticalNav } from 'shared-assets/components/Navs';
import { A } from 'shared-assets/components/Elements';
import {
  billerPermissionsNavBarLinks,
  careChecklistPermissionsNavBarLinks,
  chartPermissionsNavBarLinks,
  documentsPermissionsNavBarLinks,
  flowsheetsPermissionsNavBarLinks,
  labsPermissionsNavBarLinks,
  notesPermissionsNavBarLinks,
  recallPermissionsNavBarLinks,
} from 'shared-assets/facesheet-ui/constants';
import kfetch from '@kareo/kfetch';
import patientData from 'shared-assets/lib/patient';
import update from 'immutability-helper';
import rollbar from 'rollbar';
import Loader from 'shared-assets/components/Loader';
import lodash from 'lodash';
import { PatientInfoWidget } from 'shared-assets/facesheet-ui/components/ChartNavBar/PatientInfoWidget';
import { DateTime } from 'luxon';
import { Box, palette, theme } from '@kareo/design-system';
import {
  fetchUserPermissions,
  getUserChartAccess,
} from 'shared-assets/facesheet-ui/service/accessControl/service';
import { fetchPracticePermissions } from 'shared-assets/facesheet-ui/service/facesheet/service';
import FacesheetNavBar from 'shared-assets/facesheet-ui/components/ChartNavBar/FacesheetNavBar';

const { sortBy, intersectionBy } = lodash.noConflict();

const StyledA = styled(A)`
  &&:hover {
    background-color: var(--hover-color);
  }
`;

class ChartNavBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      linksToDisplay: billerPermissionsNavBarLinks,
      protectionSettings: {},
      userChartAccess: {},
    };
  }

  componentDidMount() {
    this.updateStyleForLegacyIframe();
    Promise.all(
      [
        fetchUserPermissions(),
        this.fetchUserChartAccess(),
        fetchPracticePermissions(),
        this.fetchPatientData(),
        this.getNavBarConfiguration(),
      ].map(p => p.catch(() => undefined)),
    )
      .then(
        ([
          userPermissions,
          userChartAccess,
          practicePermissions,
          patientData,
          navBarConfiguration,
        ]) => {
          const sortedLinks = this.getNavBarLinksByPermissions(
            navBarConfiguration,
            practicePermissions,
            userPermissions,
            userChartAccess,
          );
          this.setState({
            linksToDisplay: update(this.state.linksToDisplay, {
              $set: sortedLinks,
            }),
            loading: false,
            protectionSettings: userPermissions.protectionSettings,
            userChartAccess,
            patientData,
          });
        },
      )
      .catch(e => {
        rollbar.error(e);
        this.setState({
          loading: false,
        });
      });
  }

  togglePatientRestriction = () => {
    this.setState(prevState => ({
      ...prevState,
      userChartAccess: {
        ...prevState.userChartAccess,
        patientRestricted: !prevState.userChartAccess.patientRestricted,
      },
    }));
  };

  async getNavBarConfiguration() {
    const navBarConfiguration = [];
    await kfetch(`/facesheet-ui/api/Facesheet/views`)
      .then(configuration => {
        const enabledLinks = configuration.links.filter(link => link.enabled);
        navBarConfiguration.push(...enabledLinks);
      })
      .catch(e => {
        rollbar.error(e);
        navBarConfiguration.push(
          ...chartPermissionsNavBarLinks.filter(link => link.canBeConfigured),
        );
        navBarConfiguration.push(...labsPermissionsNavBarLinks);
        navBarConfiguration.push(...flowsheetsPermissionsNavBarLinks);
      });
    return navBarConfiguration;
  }

  async fetchUserChartAccess() {
    const patientId = this.props.patientId || this.props.match.params.patientId;
    try {
      const userChartAccess = await getUserChartAccess(patientId);
      if (userChartAccess?.ok) {
        return await userChartAccess.json();
      } else {
        return null;
      }
    } catch (error) {
      rollbar.error(error);
      return null;
    }
  }

  async fetchPatientData() {
    const patientId = this.props.patientId;
    const isModernFacesheet = this.props.isModernFacesheetDisplayed;
    // We only need patient data when rendering widget in web-app pages
    if (patientId && !isModernFacesheet) {
      try {
        const patientAPIUrl = `/notes-ui/api/Patient/${patientId}`;
        const patient = await kfetch(patientAPIUrl);
        const balance = (await patientData.getBalance(patientId))
          .remainingPatientAmount;
        const patientPhotoUrl = `${patientAPIUrl}/Photo/Img128`;
        return { ...patient, balance, patientPhotoUrl };
      } catch (error) {
        rollbar.error(error);
        return null;
      }
    }
    return null;
  }

  // Check if an individual navbar link is included in the configuration array returned from facesheet-service
  validateLink(configurationArray, navBarOption) {
    if (
      configurationArray.some(link => link.linkName === navBarOption.linkName)
    ) {
      return navBarOption;
    }
    return null;
  }

  getNavBarLinksByPermissions(
    navBarConfiguration,
    practicePermissions,
    userPermissions,
    userChartAccess,
  ) {
    const navLinksArray = [...this.state.linksToDisplay];
    let chartEnabled,
      notesEnabled,
      labsEnabled,
      flowsheetsEnabled,
      careChecklistEnabled,
      recallEnabled,
      documentsEnabled = false;

    if (
      !userPermissions.protectionSettings.isPPCEnabled ||
      !userChartAccess?.practiceAccess?.clinicalDataAccessRestrictionEnabled ||
      userChartAccess?.userAccess?.linked
    ) {
      chartEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasChartPrivilege;
      notesEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasNotesPrivilege;
      labsEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasLabsPrivilege;
      flowsheetsEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasFlowsheetsPrivilege;
      careChecklistEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasCareChecklistPrivilege;
      recallEnabled = practicePermissions.recallEnabled;
      documentsEnabled =
        userPermissions.userPrivileges.hasChartPrivilege &&
        practicePermissions.documentsEnabled;
    }

    /* Check is user has chart permissions and if they have a navbar configuration set, if so return only the
    intersection of configuration array and configurable links but also all unconfigurable links */
    if (chartEnabled) {
      const configurableLinks = [
        ...chartPermissionsNavBarLinks.filter(link => link.canBeConfigured),
      ];
      const unConfigurableLinks = [
        ...chartPermissionsNavBarLinks.filter(link => !link.canBeConfigured),
      ];
      const configuredLinks = intersectionBy(
        configurableLinks,
        navBarConfiguration,
        'linkName',
      );
      navLinksArray.push(...unConfigurableLinks.concat(configuredLinks));
    }
    if (notesEnabled) {
      navLinksArray.push(...notesPermissionsNavBarLinks);
    }
    if (labsEnabled) {
      const labLink = this.validateLink(
        navBarConfiguration,
        ...labsPermissionsNavBarLinks,
      );
      if (labLink) navLinksArray.push(labLink);
    }
    if (flowsheetsEnabled) {
      const flowsheetsLink = this.validateLink(
        navBarConfiguration,
        ...flowsheetsPermissionsNavBarLinks,
      );
      if (flowsheetsLink) navLinksArray.push(flowsheetsLink);
    }
    if (careChecklistEnabled) {
      navLinksArray.push(...careChecklistPermissionsNavBarLinks);
    }
    if (documentsEnabled) {
      navLinksArray.push(...documentsPermissionsNavBarLinks);
    }
    if (recallEnabled) {
      navLinksArray.push(...recallPermissionsNavBarLinks);
    }
    return sortBy(navLinksArray, ['displayOrder']);
  }

  getPatientDoBString() {
    const dateOfBirth = this.state.patientData.dateOfBirth;
    const dateTimeDoB = DateTime.fromMillis(dateOfBirth);
    const formattedDate = dateTimeDoB.toLocaleString(DateTime.DATE_SHORT);
    const age = Math.floor(DateTime.local().diff(dateTimeDoB, 'years').years);
    return `${formattedDate} (${age} y/o)`;
  }

  formatPatientBalance() {
    const balance = this.state.patientData.balance;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    return formatter.format(balance);
  }

  updateStyleForLegacyIframe() {
    const isTebraTheme = Cookies.get('brand') === 'cobrand-tebra';
    const backgroundColor = isTebraTheme
      ? theme.palette?.background?.default
      : palette?.background?.default;
    if (this.props.isLegacy) {
      const iframeHeader = document.querySelector('.facesheet-chrome');
      const navContainer = document.querySelector('.has-subnav');
      iframeHeader.style.display = 'none';
      navContainer.style.background = backgroundColor;
    }
  }

  render() {
    return (
      <Loader condition={!this.state.loading}>
        <If condition={this.props.isLegacy}>
          <Box height="65px" />
        </If>
        <Choose>
          <When condition={this.props.isModernFacesheetDisplayed}>
            <FacesheetNavBar
              links={this.state.linksToDisplay}
              patientId={this.props.patientId}
            />
          </When>
          <Otherwise>
            <VerticalNav
              items={[
                <If
                  condition={this.props.displayWidget && this.state.patientData}
                >
                  <PatientInfoWidget
                    patientId={this.props.patientId}
                    firstName={this.state.patientData.firstName}
                    lastName={this.state.patientData.lastName}
                    protectionSettings={this.state.protectionSettings}
                    userChartAccess={this.state.userChartAccess}
                    togglePatientRestriction={this.togglePatientRestriction}
                    patientPhotoUrl={this.state.patientData.patientPhotoUrl}
                    gender={this.state.patientData.gender.name}
                    dateOfBirth={this.getPatientDoBString()}
                    balance={this.formatPatientBalance()}
                  />
                </If>,
                <For each="navBarLink" of={this.state.linksToDisplay} index="i">
                  <StyledA
                    key={i}
                    href={navBarLink.url.replace(
                      '{patientId}',
                      this.props.patientId || this.props.match.params.patientId,
                    )}
                    target="_top"
                  >
                    {navBarLink.displayName}
                  </StyledA>
                </For>,
              ]}
              style={{ width: '12rem' }}
            />
          </Otherwise>
        </Choose>
      </Loader>
    );
  }
}
ChartNavBar.propTypes = {
  patientId: PropTypes.number,
  displayWidget: PropTypes.bool,
  isModernFacesheetDisplayed: PropTypes.bool,
  isLegacy: PropTypes.bool,
};
ChartNavBar.defaultProps = {
  displayWidget: false,
  isModernFacesheetDisplayed: false,
  isLegacy: false,
};

export default ChartNavBar;
